import { config } from "../core/config.js";
import { g_listOrigin, currentDataset, setCurrentDataset as setGlobalCurrentDataset } from "../core/variables-global.js";
import dataLoader from "./dataLoader.js";
import helpers from "../utils/helpers.js";
import initMarquesSelector from "../ui/selectionMarques.js";

let dataExchange = () => {

  // État interne (auparavant des globales implicites non déclarées)
  let data = null;
  let isLoaded = false;
  let isLoading = false;
  let loadingPromise = null;

  const eventListeners = {};

   // Event emission methods
  const addEventListener = (eventType, callback) => {
    if (!eventListeners[eventType]) {
      eventListeners[eventType] = [];
    }
    eventListeners[eventType].push(callback);
  };

  const removeEventListener = (eventType, callback) => {
    if (eventListeners[eventType]) {
      eventListeners[eventType] = eventListeners[eventType].filter(cb => cb !== callback);
    }
  };

  const emit = (eventType, eventData) => {
    if (eventListeners[eventType]) {
      eventListeners[eventType].forEach(callback => {
        try {
          callback(eventData);
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error);
        }
      });
    }
  };

    // Core data loading function for data
  const loadData = async (datasetName) => {
    console.log("loadData called with dataset:", datasetName);
    // Reset state if switching datasets
    if (datasetName && datasetName !== currentDataset) {
      data = null;
      isLoaded = false;
      isLoading = false;
      loadingPromise = null;
    }

    if (isLoaded) return data;
    if (loadingPromise) return loadingPromise;

    // Create and store the loading promise
    loadingPromise = performDataLoad();

    try {
      const result = await loadingPromise;
      return result;
    } catch (error) {
      loadingPromise = null; // Reset on error
      throw error;
    }
  };

  const performDataLoad = async () => {
    let data = null;

    try {
     const decompressed = await dataLoader.loadCSV(`${config.filesListPath[g_listOrigin]}${currentDataset}`);

      data = dataLoader.parseCSV(decompressed);
      window.currentData = data;

      // Emit data loaded event
      emit("dataLoaded", {
        dataset: currentDataset,
        data: data,
        recordCount: data.length,
        timestamp: new Date().toISOString()
      });

      // Différé pour ne pas retarder l'affichage initial du tableau : la liste des
      // marques (sélecteur de filtre) n'est pas requise pour le premier rendu, elle
      // est construite juste après que les consommateurs de "dataLoaded" aient démarré.
      setTimeout(() => createBrandList(data), 0);

      return data;
    } catch (error) {

      // Emit error event
      emit("dataError", {
        dataset: currentDataset,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  };

  // Optimized brand list creation (moved from loadData.js)
  const createBrandList = (data) => {
    if (!initMarquesSelector) {
      console.warn("initMarquesSelector n'est pas disponible");
      return;
    }

    // Use Map for better performance with large datasets
    const uniqueBrands = new Map();
    let marqueTemp = helpers.correlateFields("marque", g_listOrigin);

    for (const item of data) {
      if (item[marqueTemp] && !uniqueBrands.has(item[marqueTemp])) {
        uniqueBrands.set(item[marqueTemp], { marque: item[marqueTemp] });
      }
    }

    const brandList = Array.from(uniqueBrands.values());

    initMarquesSelector.loadMarques(brandList);
  };

    // Enhanced dataset management with validation
  const setCurrentDataset = (datasetName) => {
    if (!datasetName || typeof datasetName !== "string") {
      throw new Error("Nom du dataset invalide");
    }

    if (datasetName === currentDataset) {
      return false; // No change needed
    }

    // Reset state when changing datasets
    setGlobalCurrentDataset(datasetName);
    data = null;
    isLoaded = false;
    isLoading = false;
    loadingPromise = null;

    return true;
  };

  /**
   * Get current CSV data
   * @returns {Array|null} The currently loaded data or null
   */
  const getCurrentData = () => {
    return window.currentData || null;
  };



  /**
   * Index CSV data by a specific key field for fast lookups
   * @param {Array} dataArray - The array of CSV data objects
   * @param {string} keyField - The field name to use as the index key
   * @returns {Map} A Map where keys are field values and values are data objects
   * @example
   * const indexed = indexData(csvData, "ahri_reference_number");
   * const item = indexed.get("12345");
   */
  const indexData = (dataArray, keyField) => {
    if (!Array.isArray(dataArray)) {
      console.error("indexData: dataArray must be an array");
      return new Map();
    }

    if (!keyField || typeof keyField !== "string") {
      console.error("indexData: keyField must be a non-empty string");
      return new Map();
    }

    const index = new Map();
    dataArray.forEach(item => {
      if (item && item[keyField]) {
        index.set(item[keyField], item);
      }
    });

    return index;
  };




  return {
    loadData,
    setCurrentDataset,
    isDataLoaded: () => isLoaded,
    addEventListener,
    removeEventListener,
    indexData,
    getCurrentData
  }
};

window.dataExchange = dataExchange();
export default window.dataExchange;
